/**
 * The pen's twelve colours, the recent row, and the see-through highlighter
 * (SRS C4, docs/07-DESIGN-SYSTEM.md §2.4).
 *
 * A colour is held as its TOKEN NAME, not as a hex value, so the palette follows the
 * theme: the same swatch is a deep ink on white paper and a soft off-white in dark
 * mode. It is turned into a real colour at the moment a stroke starts, and the stroke
 * keeps that colour for good. A colour picked from the custom picker has no token, so
 * it is held as itself; both kinds go through the same two functions below.
 *
 * The hex beside each token is a fallback for the moment before the stylesheet has
 * applied and for tests, which run with no CSS at all. It is not a second source of
 * truth — the token always wins when one is available.
 */

import type { StrokeTool } from '@opal/shared';

export interface PenColor {
  /** The CSS custom property this swatch is defined by. */
  readonly token: string;
  /** What the token evaluates to in the light theme. */
  readonly fallback: string;
  /** Said aloud by a screen reader, and shown in the swatch's tooltip. */
  readonly name: string;
}

/** Colour-blind safe and legible on a projector — docs/07-DESIGN-SYSTEM.md §2.4. */
export const PEN_COLORS: readonly PenColor[] = [
  { token: '--pen-1-ink', fallback: '#1a1a2e', name: 'Ink' },
  { token: '--pen-2-red', fallback: '#e5484d', name: 'Red' },
  { token: '--pen-3-orange', fallback: '#e5793a', name: 'Orange' },
  { token: '--pen-4-amber', fallback: '#f5a524', name: 'Amber' },
  { token: '--pen-5-teal', fallback: '#12b5a8', name: 'Teal' },
  { token: '--pen-6-green', fallback: '#2a9d4e', name: 'Green' },
  { token: '--pen-7-blue', fallback: '#2e8fe5', name: 'Blue' },
  { token: '--pen-8-indigo', fallback: '#3b4ddb', name: 'Indigo' },
  { token: '--pen-9-violet', fallback: '#7c5cf0', name: 'Violet' },
  { token: '--pen-10-magenta', fallback: '#c44ba0', name: 'Magenta' },
  { token: '--pen-11-brown', fallback: '#8b6544', name: 'Brown' },
  { token: '--pen-12-grey', fallback: '#71718a', name: 'Grey' },
];

/** The colour the pen starts on: not pure black, per §2.2.1. */
export const DEFAULT_PEN_COLOR = PEN_COLORS[0]?.token ?? '--pen-1-ink';

/** docs/06-UI-SPEC.md §6.3 — one row of six under the twelve swatches. */
export const RECENT_LIMIT = 6;

/** The value behind `--highlighter-opacity`, for when no stylesheet has loaded. */
const HIGHLIGHTER_OPACITY_FALLBACK = 0.4;

/** A colour choice is a token when it names one. Anything else is a literal colour. */
export function isToken(choice: string): boolean {
  return choice.startsWith('--');
}

/** The preset a token belongs to, if it is one of the twelve. */
export function presetOf(choice: string): PenColor | undefined {
  return PEN_COLORS.find((entry) => entry.token === choice);
}

/**
 * Reads a colour token as something canvas can actually paint with.
 *
 * Read through a real `color` on a throwaway element rather than straight off the
 * root: a token defined as `var(--neutral-900)` must come back as a colour, not as
 * the text "var(--neutral-900)", which canvas would silently ignore.
 */
export function cssColor(token: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const probe = document.createElement('span');
  probe.style.color = `var(${token})`;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();
  return value === '' ? fallback : value;
}

/** A colour choice as paintable ink. Tokens are resolved; literals pass through. */
export function inkColor(choice: string): string {
  if (!isToken(choice)) return choice;
  return cssColor(choice, presetOf(choice)?.fallback ?? '#1a1a2e');
}

/** A colour choice as a CSS value, for a swatch to show itself in. */
export function swatchColor(choice: string): string {
  return isToken(choice) ? `var(${choice})` : choice;
}

/** A colour choice as a hex the browser's own colour picker will accept. */
export function hexColor(choice: string): string {
  if (!isToken(choice)) return choice;
  return presetOf(choice)?.fallback ?? '#1a1a2e';
}

/** A plain number token, such as the highlighter's opacity. */
function cssNumber(token: string, fallback: number): number {
  if (typeof document === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(token);
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : fallback;
}

/** SRS C4 — the highlighter is see-through; the pen is not. */
export function opacityFor(tool: StrokeTool): number {
  if (tool !== 'highlighter') return 1;
  return cssNumber('--highlighter-opacity', HIGHLIGHTER_OPACITY_FALLBACK);
}

/**
 * The recent row after a colour has been used.
 *
 * Most recent first, never the same colour twice, never more than six — so the row
 * is somewhere the teacher can reliably find the two or three colours of this lesson.
 */
export function addRecentColor(recent: readonly string[], color: string): readonly string[] {
  return [color, ...recent.filter((used) => used !== color)].slice(0, RECENT_LIMIT);
}
